import { ReactElement, useEffect, useRef } from 'react';
import { useGenerateHealthGraph } from '../../hooks/useGenerateHealthGraph';
import { colorByHealthVariant, parseHealthVariantLabel } from '../../helpers';
import { HealthVariant } from '../UnitsInfo/types';
import { GetUnitHealthHistory } from 'pages/NewOverview/types';
import { colors } from 'styles/colors';
import { useTranslation } from 'react-i18next';
import { Legend } from 'pages/NewOverview/components/History/Legend';
import { GraphInnerContainer, LegendContainer } from 'pages/NewOverview/components/History/styles';

interface HealthGraphProps {
  data: GetUnitHealthHistory | undefined;
}

export function HealthGraph({
  data,
}: Readonly<HealthGraphProps>): ReactElement {
  const chartReference = useRef<HTMLDivElement>(null);

  const { renderHealthChart } = useGenerateHealthGraph(chartReference, !data);

  useEffect(() => renderHealthChart(data), [data]);

  return (
    <>
      <GraphInnerContainer containerId="health-card">
        <div id="health-card" ref={chartReference} style={{ width: '100%', height: '100%' }} />
      </GraphInnerContainer>
      <LegendGraph disabled={!data} />
    </>
  );
}

function LegendGraph({ disabled }: Readonly<{ disabled: boolean }>): ReactElement {
  return (
    <LegendContainer>
      {disabled ? (
        <HealthLegend variant="no-data" />
      ) : (
        <>
          <HealthLegend variant="healthy" />
          <HealthLegend variant="alert" />
          <HealthLegend variant="critical" />
          <HealthLegend variant="inoperative" />
        </>
      )}
    </LegendContainer>
  );
}

function HealthLegend({ variant }: Readonly<{ variant: HealthVariant | 'no-data' }>): ReactElement {
  const { t } = useTranslation();

  if (variant === 'no-data') {
    return (
      <Legend
        color={colors.icon['disabled-300']}
        label={t('semDados')}
      />
    );
  }

  return (
    <Legend
      color={colorByHealthVariant(variant)}
      label={parseHealthVariantLabel(variant)}
    />
  );
}
